<template>
  <div
    ref="dropdownRef"
    :data-testid="dataTestid"
    class="relative h-full"
  >
    <button
      class="flex items-center text-semantic-primary-on-light h-full px-2 hover:text-semantic-inverse-primary"
      @click.prevent="toggleDropdown"
    >
      <span class="lg:mr-3 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-semantic-surface-container-low text-sm font-bold text-semantic-primary-default">
        <ClientOnly>
          <!-- Profile image (shows when available & not errored) -->
          <img
            v-if="showProfilePhoto"
            :alt="userDetails.given_name"
            :src="userDetails.picture"
            width="40"
            height="40"
            class="h-full w-full object-cover"
            @error="onProfileImageError"
          />
          <!-- Fallback initials avatar -->
          <span v-else>{{ initials }}</span>
          <template #fallback>
            <span>{{ initials }}</span>
          </template>
        </ClientOnly>
      </span>

      <span
        class="mr-1 font-bold text-sm hidden lg:block whitespace-nowrap max-w-40 truncate text-semantic-surface-default"
        :class="{
          'text-semantic-inverse-primary': dropdownOpen
        }"
      >
        {{ userDetails.given_name }}
      </span>

      <IconsChevronDown
        class="text-semantic-surface-default"
        :class="{
          'hidden lg:block': true,
          'rotate-180': dropdownOpen
        }" />
    </button>

    <!-- Dropdown Start -->
    <div
      v-if="dropdownOpen"
      class="absolute right-0 z-[999] flex w-[calc(100vw-2rem)] sm:w-[375px] flex-col items-start gap-6 rounded-b-2xl border-0 bg-semantic-secondary-variant-highlight p-6 shadow-xl"
    >
      <div class="flex w-full flex-col items-center gap-1">
        <div class="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full bg-semantic-primary-container text-[40px] font-normal leading-[1.2] text-semantic-secondary-default">
          <img
            v-if="showProfilePhoto"
            :alt="userDetails.given_name"
            :src="userDetails.picture"
            class="h-full w-full object-cover"
            @error="onProfileImageError"
          />
          <span v-else>{{ initials }}</span>
        </div>

        <span class="mt-2 block max-w-full truncate text-center font-bold text-lg text-semantic-primary-on-light">
          {{ userDetails.full_name }}
        </span>

        <div class="flex flex-wrap items-center justify-center gap-2">
          <span class="max-w-[10rem] truncate text-xs text-semantic-primary-on-light">
            {{ userDetails.email }}
          </span>
          <atoms-badge
            theme="inverted"
            size="small"
            class="!rounded-sm !px-2 !py-0.5 !text-[0.625rem] uppercase"
          >
            {{ userStore.isEmailVerified ? 'Verified' : 'Unverified' }}
          </atoms-badge>
        </div>
      </div>

      <ul class="flex w-full flex-col gap-1 border-b border-semantic-surface-container-low pb-4">
        <li>
          <NuxtLink
            id="dashboard_nav_btn"
            to="/dashboard/"
            class="flex items-center gap-3 px-3 py-2 font-medium rounded-lg group text-sm text-semantic-primary-on-default hover:bg-white/5 hover:text-semantic-primary-on-light"
          >
            <V2IconsCirclesFour
              width="24"
              height="24"
              fill="currentColor"
              class="text-semantic-primary-on-default group-hover:text-semantic-primary-on-light"
            />
            Dashboard
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            id="account_settings_nav_btn"
            to="/dashboard/account-settings/"
            class="flex items-center gap-3 px-3 py-2 font-medium rounded-lg group text-sm text-semantic-primary-on-default hover:bg-white/5 hover:text-semantic-primary-on-light"
          >
            <V2IconsCog
              width="24"
              height="24"
              fill="currentColor"
              class="text-semantic-primary-on-default group-hover:text-semantic-primary-on-light"
            />
            My Profile
          </NuxtLink>
        </li>
      </ul>
      <NuxtLink
        id="auth_logout_btn"
        type="button"
        class="flex w-full items-center gap-3 px-3 py-2 font-medium rounded-lg group text-sm text-semantic-primary-on-default hover:bg-white/5 hover:text-semantic-primary-on-light cursor-pointer"
        @click="signOut"
      >
        <V2IconsSignOut
          width="24"
          height="24"
          fill="currentColor"
          class="text-semantic-primary-on-default group-hover:text-semantic-primary-on-light"
        />
        Sign out
      </NuxtLink>
    </div>
    <!-- Dropdown End -->
  </div>
</template>

<script setup>
import IconsChevronDown from '@/components/icons/ChevronDown.vue'

defineProps({
  dataTestid: {
    type: String,
    default: ''
  }
})

const userStore = useUserStore()
const appStore = useLayoutsStore()
const router = useRouter()

const dropdownOpen = ref(false)
const dropdownRef = ref(null)

const userDetails = computed(() => userStore.getUserProfile)

const initials = computed(() => {
  const names = [userDetails.value?.given_name, userDetails.value?.family_name].filter(Boolean)
  if (!names.length && userDetails.value?.email) return userDetails.value.email.slice(0, 2).toUpperCase()
  return names.map(name => name.slice(0, 1)).join('').slice(0, 2).toUpperCase()
})

// Dual avatar: computed visibility & error tracking
const profileImageErrored = ref(false)
const showProfilePhoto = computed(() => !!userDetails.value?.picture && !profileImageErrored.value)

function onProfileImageError () {
  profileImageErrored.value = true
}

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value
}

const closeDropdown = () => {
  dropdownOpen.value = false
}

const signOut = () => {
  // Sign Out
  appStore.setPageLoader(true)
  nextTick().then(() => {
    userStore.logout()
      .finally(() => {
        sessionStorage.removeItem('is_apple_relay_email')
        router.push(generateLoginPageUrl())
        appStore.setPageLoader(false)
      })
  })
  closeDropdown()
}

const handleClickOutside = event => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
